import { PricingPlan } from "@/primary-models";

import {
  ALL_MEAL_PLANS,
  MEAL_PLAN_FREEZER,
  MEAL_PLAN_HALF,
  MEAL_PLAN_SAVER,
} from "./mealPlanSuggestions";
import { CancellationReasonForMealPlan } from "./cancellation-reason";

export const getCurrentMealPlanOptions = (currentMealPlan: PricingPlan) => {
  // First check if it's in the ALL_MEAL_PLANS array
  const planFromArray = ALL_MEAL_PLANS.find((mealPlan) => mealPlan.value === currentMealPlan);
  if (planFromArray) {
    return planFromArray;
  }

  // If not found in ALL_MEAL_PLANS, check individual plan objects
  // This handles cases like MEAL_PLAN_FREEZER which is not in ALL_MEAL_PLANS
  if (currentMealPlan === PricingPlan.FREEZER) {
    return MEAL_PLAN_FREEZER;
  }

  return undefined;
};

export function getMealPlanSuggestions({
  cancellationReason,
  currentMealPlan,
  selectedMealPlan,
}:{
  cancellationReason?: CancellationReasonForMealPlan;
  currentMealPlan?: PricingPlan;
  selectedMealPlan?: PricingPlan;
}) {
  // if any of the required parameters are not provided, we fallback to suggesting all meal plans
  if (!currentMealPlan || !cancellationReason) {
    return ALL_MEAL_PLANS
      .map((mealPlan) => ({
        ...mealPlan,
        advanced_options: { ...mealPlan.advanced_options, current: mealPlan.value === currentMealPlan },
        selected: selectedMealPlan === mealPlan.value,
      }));
  }

  const currentMealPlanOptions = getCurrentMealPlanOptions(currentMealPlan);

  // if the current meal plan is not found, we fallback to suggesting all meal plans
  if (!currentMealPlanOptions) {
    return ALL_MEAL_PLANS
      .map((mealPlan) => ({
        ...mealPlan,
        advanced_options: { ...mealPlan.advanced_options, current: mealPlan.value === currentMealPlan },
        selected: selectedMealPlan === mealPlan.value,
      }));
  }

  // If the user is cancelling because the meal plan is too expensive and they are not on the saver plan,
  // we suggest their current meal plan and the saver plan
  if (
    cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_OVER_MY_BUDGET &&
    currentMealPlan !== PricingPlan.SAVER
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...MEAL_PLAN_SAVER,
        selected: selectedMealPlan === PricingPlan.SAVER,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive and they are on the saver plan,
  // we suggest the half meal plan and their current meal plan
  if (
    cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_OVER_MY_BUDGET &&
    currentMealPlan === PricingPlan.SAVER
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlan === PricingPlan.HALF,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive compared to other brands
  // and they are on the standard or freezer plan, we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_OTHER_BRANDS_ARE_CHEAPER &&
      (currentMealPlan === PricingPlan.STANDARD || currentMealPlan === PricingPlan.FREEZER)
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...MEAL_PLAN_SAVER,
        selected: selectedMealPlan === PricingPlan.SAVER,
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlan === PricingPlan.HALF,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive compared to other brands
  // and they are on any other meal plan, we suggest all meal plans
  if (cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_OTHER_BRANDS_ARE_CHEAPER) {
    return ALL_MEAL_PLANS.map((mealPlan) => ({
      ...mealPlan,
      selected: selectedMealPlan === mealPlan.value,
      advanced_options: {
        ...mealPlan.advanced_options,
        current: mealPlan.value === currentMealPlan,
      },
    }));
  }

  // If the user is cancelling because they think it's not worth the cost
  // and they are on the standard or freezer plan,
  // we suggest their current meal plan, the half meal plan, and the saver plan
  if (
    cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_NOT_WORTH_THE_COST &&
    (currentMealPlan === PricingPlan.STANDARD || currentMealPlan === PricingPlan.FREEZER)
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...MEAL_PLAN_SAVER,
        selected: selectedMealPlan === PricingPlan.SAVER,
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlan === PricingPlan.HALF,
      },
    ];
  }

  // If the user is cancelling because they think it's not worth the cost
  // and they are on any other meal plan, we suggest all meal plans
  if (cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_NOT_WORTH_THE_COST) {
    return ALL_MEAL_PLANS.map((mealPlan) => ({
      ...mealPlan,
      selected: selectedMealPlan === mealPlan.value,
      advanced_options: {
        ...mealPlan.advanced_options,
        current: mealPlan.value === currentMealPlan,
      },
    }));
  }

  // If the user is cancelling because they have accumulated too much meal in their freezer
  // and they are on any plan except the freezer plan, we suggest their current meal plan and the freezer plan
  if (
    cancellationReason === CancellationReasonForMealPlan.FREEZER_SPACE_ACCUMULATION &&
    currentMealPlan !== PricingPlan.FREEZER
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...MEAL_PLAN_FREEZER,
        selected: selectedMealPlan === PricingPlan.FREEZER,
      },
    ];
  }

  // If the user is cancelling because they have accumulated too much meal in their freezer
  // and they are on the freezer plan, we suggest all meal plans except the freezer plan
  if (cancellationReason === CancellationReasonForMealPlan.FREEZER_SPACE_ACCUMULATION) {
    return ALL_MEAL_PLANS
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlan === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlan,
        },
      }));
  }

  // if the user is cancelling because they don't have enough space in their freezer
  // and they are on any plan except the freezer plan, we suggest their current meal plan and the freezer plan
  if (
    cancellationReason === CancellationReasonForMealPlan.FREEZER_SPACE_NOT_ENOUGH_SPACE &&
    currentMealPlan !== PricingPlan.FREEZER
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...MEAL_PLAN_FREEZER,
        selected: selectedMealPlan === PricingPlan.FREEZER,
      },
    ];
  }

  // If the user is cancelling because they don't have enough space in their freezer
  // and they are on the freezer plan, we suggest all meal plans except the freezer plan
  if (cancellationReason === CancellationReasonForMealPlan.FREEZER_SPACE_NOT_ENOUGH_SPACE) {
    return ALL_MEAL_PLANS
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlan === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlan,
        },
      }));
  }

  // If the user is cancelling because they think the subscription doesn't work for them and they need less food
  // and they are on any other meal plan except the half meal plan,
  // we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReasonForMealPlan.SUBSCRIPTION_DOESNT_WORK_NEED_LESS_FOOD &&
    currentMealPlan !== PricingPlan.HALF
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlan === MEAL_PLAN_HALF.value,
      },
    ];
  }

  // If the user is cancelling because they think the subscription doesn't work for them and they need less food
  // and they are on the half meal plan, we suggest all meal plans except the half meal plan
  if (cancellationReason === CancellationReasonForMealPlan.SUBSCRIPTION_DOESNT_WORK_NEED_LESS_FOOD) {
    return ALL_MEAL_PLANS
      .filter((mealPlan) => mealPlan.value !== PricingPlan.HALF)
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlan === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlan,
        },
      }));
  }

  // if the user is cancelling but they plan to to come back as
  // they are currently feeding their cat with other foods alongside Katkin
  // and they are on any plan except the half meal plan, we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReasonForMealPlan.COMING_BACK_FEEDING_OTHER_FOODS_ALONGSIDE_KATKIN &&
    currentMealPlan !== PricingPlan.HALF
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlan === MEAL_PLAN_HALF.value,
      },
    ];
  }

  // if the user is cancelling but they plan to come back as
  // they are currently feeding their cat with other foods alongside Katkin
  // and they are on the half meal plan, we suggest all meal plans except the half meal plan
  if (cancellationReason === CancellationReasonForMealPlan.COMING_BACK_FEEDING_OTHER_FOODS_ALONGSIDE_KATKIN) {
    return ALL_MEAL_PLANS
      .filter((mealPlan) => mealPlan.value !== PricingPlan.HALF)
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlan === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlan,
        },
      }));
  }

  // if the user is cancelling for any other reason, we suggest all meal plans
  return ALL_MEAL_PLANS.map((mealPlan) => ({
    ...mealPlan,
    selected: selectedMealPlan === mealPlan.value,
    advanced_options: {
      ...mealPlan.advanced_options,
      current: mealPlan.value === currentMealPlan,
    },
  }));
}
